import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { ClipCategory, ClipLabelEdit, ClipLabelVocabulary, LibraryClipLabel } from 'services/VideoEditingService';
import ClipLabelFields, { Draft, clipLabelEditFrom, draftFrom } from 'pages/VideoEditing/components/ClipLabelFields';
import { ClipTrimmerRange } from 'pages/VideoEditing/components/ClipTrimmer';
import { formatDuration } from 'pages/VideoEditing/format';

interface SaveAsNewClipDialogProps {
  // The clip the new one is being carved out of. Its settings seed every field.
  source: LibraryClipLabel;
  // The range chosen on the trimmer behind this dialog. Read-only here — it was just set.
  range: ClipTrimmerRange;
  vocabulary: ClipLabelVocabulary | null;
  categories: ClipCategory[] | null;
  onCreateCategory: (name: string) => Promise<ClipCategory>;
  onClose: () => void;
  onSave: (edit: ClipLabelEdit) => Promise<void>;
}

/**
 * Name and describe a clip being carved out of another one.
 *
 * Prepopulated from the source, because the new clip is the same footage: re-deriving its
 * product and tags by hand would be busywork, and re-typing them invites drift. But every field
 * is editable — the whole point of carving a piece out is usually that it shows something more
 * specific than the take it came from, and the previous version silently copied the source's
 * classification with only a cramped name box to show for it.
 */
export default function SaveAsNewClipDialog({
  source, range, vocabulary, categories, onCreateCategory, onClose, onSave,
}: SaveAsNewClipDialogProps) {
  // Seeded from the source, with the name cleared: a copy called "hands squeezing" that is
  // actually a different moment is worse than one that is obviously unnamed.
  const [draft, setDraft] = useState<Draft>(() => ({ ...draftFrom(source), name: '' }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await onSave(clipLabelEditFrom(draft, { categoriesOffered: !!categories }));
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not save that clip');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Save as a new clip
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          A new library entry from{' '}
          <Chip
            size="small"
            label={`${formatDuration(range.inSec)} – ${formatDuration(range.outSec)}`}
            sx={{ height: 18, fontSize: 11 }}
          />{' '}
          of “{source.name || source.fileName}”. The original is left as it is.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Everything below starts as the clip this came from — change whatever differs.
        </Typography>
        {/* No Confidence field: a clip a person creates is human-saved by definition, so the
            model's certainty about its own product guess is not a question to ask here. */}
        <ClipLabelFields
          draft={draft}
          onDraftChange={setDraft}
          vocabulary={vocabulary}
          categories={categories}
          onCreateCategory={onCreateCategory}
          autoFocusName
          showConfidence={false}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {saving ? 'Saving…' : 'Save new clip'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
